import React, { useState } from 'react'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { ContentModal } from 'components/organisms/modals/content-modal'
import { PieImage } from 'components/common'
import type { EventsManageAttendeeBookingsQueryEventType } from 'pages/RepClubs/Events/event.type'

interface ApproveAllConfirmationModalProps {
	open: boolean
	setOpen: (open: boolean) => void
	event?: Partial<EventsManageAttendeeBookingsQueryEventType> | null
	pendingAttendees: number
	onConfirm: () => void
	loading?: boolean
}

export const ApproveAllConfirmationModal: React.FC<ApproveAllConfirmationModalProps> = ({
	open,
	setOpen,
	event,
	pendingAttendees,
	onConfirm,
	loading = false,
}) => {
	return (
		<ContentModal
			open={open}
			setOpen={setOpen}
			isModal
			className="sm:py-[56px] sm:px-[72px]"
			hideHeader={true}
			customSize="sm:!max-w-[474px]"
		>
			<div className="relative flex flex-col items-center">
				<div>
					<div>
						<h3 className="text-2xl font-bold flex justify-center items-center gap-2">Approve All</h3>
					</div>
					<div className="mt-5">
						You are approving {pendingAttendees} pending {pendingAttendees > 1 ? 'attendees' : 'attendee'}{' '}
						for this event
					</div>
					<div className="flex justify-center items-center py-9 relative">
						<PieImage
							className="w-[200px] h-auto rounded-xl overflow-hidden flex-shrink-0"
							images={[event?.avatarUrl]}
						/>
					</div>
					<div>
						Are you sure you would like to approve all {pendingAttendees} pending{' '}
						{pendingAttendees > 1 ? 'attendees' : 'attendee'} for this event?
					</div>
				</div>
				<div className="flex justify-between w-full gap-4 mt-[43px]">
					<button
						type="button"
						className="w-[157px] rounded-3xl text-center transition-all duration-300 bg-primary text-white font-bold block h-10 shadow-button backdrop-blur-lg hover:bg-primary-hover disabled:bg-white/10 disabled:text-white-25 disabled:shadow-none"
						onClick={onConfirm}
						disabled={loading}
					>
						{loading && <FontAwesomeIcon icon={['fas', 'spinner']} size="lg" spin className="mr-1" />}
						Confirm
					</button>
					<button
						type="button"
						className="w-[157px] rounded-3xl text-center transition-all duration-300 bg-secondary-button text-white font-bold block h-10 shadow-button backdrop-blur-lg hover:bg-secondary-button-hover disabled:bg-white/10 disabled:text-white-25 disabled:shadow-none"
						onClick={() => setOpen(false)}
					>
						Cancel
					</button>
				</div>
			</div>
		</ContentModal>
	)
}

// ---------------------------------------------------------------------------
// Modal Manager integration
// ---------------------------------------------------------------------------

export interface ApproveAllConfirmationModalManagedProps {
	event?: Partial<EventsManageAttendeeBookingsQueryEventType> | null
	pendingAttendees: number
	onConfirm: () => void
	loading?: boolean
}

declare module 'components/organisms/modals/modal-registry' {
	interface ModalRegistry {
		'approve-all-confirmation': ApproveAllConfirmationModalManagedProps
	}
}

export function ManagedApproveAllConfirmationModal({
	onClose,
	onConfirm,
	...rest
}: ApproveAllConfirmationModalManagedProps & { onClose: () => void }) {
	const [open, setOpen] = useState(true)

	const handleClose = () => {
		setOpen(false)
		setTimeout(onClose, 200)
	}

	return (
		<ApproveAllConfirmationModal
			open={open}
			setOpen={(v: boolean) => { if (!v) handleClose() }}
			onConfirm={() => { onConfirm(); handleClose() }}
			{...rest}
		/>
	)
}
